import { authenticatedHttpClient } from './httpClient';
import {
  Supplier,
  SupplierCreatePayload,
  SUPPLIER_LIST_PAGE_SIZE,
  SupplierQueryResult,
  SupplierUpdatePayload,
} from '../models/supplier';

export const listSuppliers = async (
  organizationId: string,
  options?: {
    q?: string;
    activeOnly?: boolean;
    limit?: number;
    cursor?: string;
  }
): Promise<SupplierQueryResult> => {
  const params = new URLSearchParams();
  if (options?.q) params.append('q', options.q);
  if (options?.activeOnly) params.append('active_only', 'true');
  params.append('limit', String(options?.limit ?? SUPPLIER_LIST_PAGE_SIZE));
  if (options?.cursor) params.append('cursor', options.cursor);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<SupplierQueryResult>(
    `/suppliers/${organizationId}${suffix}`
  );
};

/** Load all active suppliers (paginated API loop) for purchase pickers. */
export const listAllActiveSuppliers = async (
  organizationId: string
): Promise<Supplier[]> => {
  const items: Supplier[] = [];
  let cursor: string | undefined;
  let hasMore = true;

  while (hasMore) {
    const result = await listSuppliers(organizationId, {
      activeOnly: true,
      limit: 50,
      cursor,
    });
    items.push(...(result.items || []));
    hasMore = Boolean(result.hasMore && result.nextCursor);
    cursor = result.nextCursor ?? undefined;
    if (!cursor) {
      hasMore = false;
    }
  }

  return items;
};

export const getSupplier = async (
  organizationId: string,
  supplierId: string
): Promise<Supplier> => {
  return authenticatedHttpClient.get<Supplier>(
    `/suppliers/${organizationId}/${supplierId}`
  );
};

export const createSupplier = async (
  organizationId: string,
  payload: SupplierCreatePayload
): Promise<Supplier> => {
  return authenticatedHttpClient.post<Supplier>(
    `/suppliers/${organizationId}`,
    payload
  );
};

export const updateSupplier = async (
  organizationId: string,
  supplierId: string,
  payload: SupplierUpdatePayload
): Promise<Supplier> => {
  return authenticatedHttpClient.put<Supplier>(
    `/suppliers/${organizationId}/${supplierId}`,
    payload
  );
};

export const deleteSupplier = async (
  organizationId: string,
  supplierId: string
): Promise<{ ok: boolean }> => {
  return authenticatedHttpClient.delete<{ ok: boolean }>(
    `/suppliers/${organizationId}/${supplierId}`
  );
};

export interface SupplierBulkImportResult {
  created: number;
  updated: number;
  errors: number;
  total_processed: number;
}

export const bulkImportSuppliers = async (
  organizationId: string,
  rows: SupplierCreatePayload[]
): Promise<SupplierBulkImportResult> => {
  return authenticatedHttpClient.post<SupplierBulkImportResult>(
    `/suppliers/${organizationId}/bulk`,
    { json: rows }
  );
};
